import type {APIResponse, ObjectWithMeta} from '@/api/Api';
import {Api} from '@/api/Api';
import {deserialize} from './Deserializer';
import type {Project} from '@/models/Project';
import {ProjectSchema} from "@/models/Project";
import formatRFQDateTime from "@/use/formatRFQDateTime";

export default class ProjectAPI extends Api {

  index(params: any): Promise<ObjectWithMeta<Project>> {
    return this.get({
      url: 'projects',
      queryParams: params
    })
    .then((res) => this.processResponse(res))
    .then(res => {
      res.data = res?.data?.map((project: any) => {
        project.attributes.sub_bid_due = formatRFQDateTime(project.attributes.sub_bid_date, project.attributes.sub_bid_time);
        project.attributes.site_meeting_at = formatRFQDateTime(project.attributes.site_meeting_date, project.attributes.site_meeting_time);
        project.attributes.rfi_due_at = formatRFQDateTime(project.attributes.rfi_due_date, project.attributes.rfi_due_time);
        return project;
      });
      return {
        links: res.links,
        meta: res.meta,
        data: deserialize<Project>(res, ProjectSchema) as Project[],
      };
    });
  }

  show(id: number, params: any): Promise<Project> {
    return this.get({
      url: `projects/${id}`,
      queryParams: params
    })
    .then((res) => this.processResponse(res))
    .then((res) => {
      res.data.attributes.sub_bid_due = formatRFQDateTime(res.data.attributes.sub_bid_date, res.data.attributes.sub_bid_time);
      res.data.attributes.site_meeting_at = formatRFQDateTime(res.data.attributes.site_meeting_date, res.data.attributes.site_meeting_time);
      res.data.attributes.rfi_due_at = formatRFQDateTime(res.data.attributes.rfi_due_date, res.data.attributes.rfi_due_time);
      // An included relationship may be null when the API filters an
      // inaccessible bid package from a project response. Ignore those
      // placeholders before normalizing the remaining resources.
      res.included = res.included?.filter(Boolean).map((obj: any) => {
        if (obj.type === "bidPackage") {
          return {
            ...obj,
            attributes: {
              ...obj.attributes,
              sub_bid_due: formatRFQDateTime(obj.attributes.sub_bid_date, obj.attributes.sub_bid_time),
              site_meeting_at: formatRFQDateTime(obj.attributes.site_meeting_date, obj.attributes.site_meeting_time),
              rfi_due_at: formatRFQDateTime(obj.attributes.rfi_due_date, obj.attributes.rfi_due_time),
            },
          }
        }
        else {
          return obj;
        }
      });
      return res;
    })
    .then(res => deserialize<Project>(res, ProjectSchema) as Project);
  }

  showPublic(id: number, params: any): Promise<Project> {
    return this.get({
      url: `projects/${id}/public`,
      queryParams: params
    })
    .then((res) => this.processResponse(res))
    .then((res) => {
      res.data.attributes.sub_bid_due = formatRFQDateTime(res.data.attributes.sub_bid_date, res.data.attributes.sub_bid_time);
      res.data.attributes.site_meeting_at = formatRFQDateTime(res.data.attributes.site_meeting_date, res.data.attributes.site_meeting_time);
      res.data.attributes.rfi_due_at = formatRFQDateTime(res.data.attributes.rfi_due_date, res.data.attributes.rfi_due_time);
      return res;
    })
    .then(res => deserialize<Project>(res, ProjectSchema) as Project);
  }

  accessCode(id: number, data: any): Promise<APIResponse> {
    return this.post({
      url: `projects/${id}/access-code`,
      headers: [],
      body: JSON.stringify(data),
    })
    .then((res) => this.processResponse(res))
  }

  requestAccess(id: number, data: any): Promise<APIResponse> {
    return this.post({
      url: `projects/${id}/request-access`,
      headers: [],
      body: JSON.stringify(data),
    })
    .then((res) => this.processResponse(res))
  }

  acceptNda(id: number, data: any): Promise<APIResponse> {
    return this.post({
      url: `projects/${id}/accept-nda`,
      headers: [],
      body: JSON.stringify(data),
    })
    .then((res) => this.processResponse(res))
  }
};
